import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { ReportTemplate, TiptapJSON } from '../types/template.types';
import {
  createTemplateService,
  TemplateService,
} from '../services/template.service';

// Import hardcoded templates
import { neurocutaneousTemplate } from '../templates/neurocutaneous-template';

/**
 * Available hardcoded templates
 */
const HARDCODED_TEMPLATES: Record<string, ReportTemplate> = {
  'neurocutaneous-syndromes': neurocutaneousTemplate,
};

interface UseReportTemplateReturn {
  /** Currently loaded template */
  template: ReportTemplate | null;
  /** Loading state */
  loading: boolean;
  /** Error if loading failed */
  error: string | null;
  /** Load a template by ID (checks hardcoded first, then Medplum) */
  loadTemplate: (templateId: string) => Promise<void>;
  /** Save template to Medplum */
  saveTemplate: (template: ReportTemplate) => Promise<void>;
  /** List all available templates */
  listTemplates: () => Promise<{ id: string; name: string }[]>;
  /** Get hardcoded template by ID */
  getHardcodedTemplate: (templateId: string) => ReportTemplate | null;
}

/**
 * Hook for managing report templates
 * Supports both hardcoded templates and Medplum-stored templates
 * @returns Template state and operations
 */
export function useReportTemplate(): UseReportTemplateReturn {
  const medplum = useMedplum();
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [service, setService] = useState<TemplateService | null>(null);

  // Initialize service when medplum is available
  useEffect(() => {
    if (medplum) {
      setService(createTemplateService(medplum));
    }
  }, [medplum]);

  /**
   * Get a hardcoded template by ID
   */
  const getHardcodedTemplate = useCallback(
    (templateId: string): ReportTemplate | null => {
      return HARDCODED_TEMPLATES[templateId] || null;
    },
    []
  );

  /**
   * Load a template by ID
   * First checks hardcoded templates, then falls back to Medplum
   */
  const loadTemplate = useCallback(
    async (templateId: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        // First check hardcoded templates
        const hardcoded = getHardcodedTemplate(templateId);
        if (hardcoded) {
          setTemplate(hardcoded);
          return;
        }

        // Fall back to Medplum storage
        if (!service) {
          throw new Error('Template service not initialized');
        }

        const loaded = await service.loadTemplate(templateId);
        if (!loaded) {
          throw new Error(`Template not found: ${templateId}`);
        }

        setTemplate(loaded);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load template';
        setError(message);
        console.error('Error loading template:', err);
      } finally {
        setLoading(false);
      }
    },
    [service, getHardcodedTemplate]
  );

  /**
   * Save a template to Medplum
   */
  const saveTemplate = useCallback(
    async (templateToSave: ReportTemplate): Promise<void> => {
      if (!service) {
        throw new Error('Template service not initialized');
      }

      setLoading(true);
      setError(null);

      try {
        await service.saveTemplate(templateToSave);
        setTemplate(templateToSave);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save template';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service]
  );

  /**
   * List all available templates (hardcoded + Medplum)
   */
  const listTemplates = useCallback(async (): Promise<
    { id: string; name: string }[]
  > => {
    // Start with hardcoded templates
    const templates = Object.entries(HARDCODED_TEMPLATES).map(([id, t]) => ({
      id,
      name: t.name,
    }));

    // Add Medplum templates if service is available
    if (service) {
      try {
        const medplumTemplates = await service.listTemplates();
        // Filter out duplicates (hardcoded takes precedence)
        for (const t of medplumTemplates) {
          if (!templates.some((existing) => existing.id === t.id)) {
            templates.push({ id: t.id, name: t.name });
          }
        }
      } catch (err) {
        console.warn('Failed to load Medplum templates:', err);
      }
    }

    return templates;
  }, [service]);

  return {
    template,
    loading,
    error,
    loadTemplate,
    saveTemplate,
    listTemplates,
    getHardcodedTemplate,
  };
}

interface UseReportContentReturn {
  /** Saved report content */
  content: TiptapJSON | undefined;
  /** Loading state */
  loading: boolean;
  /** Error if operation failed */
  error: string | null;
  /** Load saved content for a report */
  loadContent: (reportId: string) => Promise<void>;
}

/**
 * Hook for managing saved report content
 * @returns Content state and operations
 */
export function useReportContent(): UseReportContentReturn {
  const medplum = useMedplum();
  const [content, setContent] = useState<TiptapJSON | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [service, setService] = useState<TemplateService | null>(null);

  useEffect(() => {
    if (medplum) {
      setService(createTemplateService(medplum));
    }
  }, [medplum]);

  const loadContent = useCallback(
    async (reportId: string): Promise<void> => {
      if (!service) {
        throw new Error('Template service not initialized');
      }

      setLoading(true);
      setError(null);

      try {
        const loaded = await service.loadReportContent(reportId);
        setContent(loaded);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load report content';
        setError(message);
        console.error('Error loading report content:', err);
      } finally {
        setLoading(false);
      }
    },
    [service]
  );

  return {
    content,
    loading,
    error,
    loadContent,
  };
}
